import { Cog8ToothIcon, GlobeAltIcon, Square3Stack3DIcon, UsersIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { classNames } from "src/helpers/classNames";
import Tabs from "src/components/Shared/Tabs/Tabs";
import ClientSetup from "./Setup/ClientSetup";
import { connect } from "react-redux";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Preloader from "src/components/Shared/LoadingAnimations/Preloader";
import EnvironmentSetup from "./Setup/EnvironmentSetup";
import { ClientOverview } from "./ClientOverview";
import ClientPlanDetails from "./ClientTransactions/ClientPlanDetails";
import ClientUserList from "./ClientAdmins/ClientUserList";
import { apiRequest } from "src/async/apiUtils";
import { clientTypes } from "src/helpers/clientHelper";
import AssignPlanSlider from "./ClientTransactions/AssignPlanSlider";
import useFetch from "src/hooks/useFetch";

const ClientBodyContent = ({ data = null, statuses = null, handleTypeChange = () => { }, setLists = () => { }, isEditing, digitaloceanDomains = [], ...props }) => {
  const [tabs, setTabs] = useState([]);
  const [currentTab, setCurrentTab] = useState(null);

  const [planDetails, setPlanDetails] = useState(null);
  const [planDetailsLoaded, setPlanDetailsLoaded] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);

  const [defaultUser, setDefaultUser] = useState(null);

  const domain = useMemo(() => {
    const customDomain = data?.custom_domain;

    const isActive = customDomain ? digitaloceanDomains?.find((domain) => domain?.spec?.domain === customDomain)?.phase === "ACTIVE" : false;

    return isActive ? customDomain : data?.domain;
  }, [data?.custom_domain, data?.domain, digitaloceanDomains]);

  const {
    response: { data: plans },
  } = useFetch(`/plans`);

  const getDefaultUser = async () => {
    try {
      const res = await apiRequest("get", `/clients/:client_id/users/default_user`, {
        params: { client_id: data._id },
      });
      setDefaultUser(res?.data?.data);
    } catch (error) { }
  };

  const getDefaultUserImage = async () => {
    try {
      const res = await apiRequest("get", `/clients/:client_id/users/:user_id/image`, {
        params: { client_id: data?._id, user_id: defaultUser?._id },
      });
      setDefaultUser((prev) => ({ ...prev, ...res?.data?.data }));
    } catch (error) { }
  };

  useEffect(() => {
    if (data?._id) {
      getDefaultUser();
    }

    if (defaultUser?._id && data?._id) {
      getDefaultUserImage();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?._id, defaultUser?._id]);

  const getPlanDetails = useCallback(async () => {
    if (!data?._id) return;
    setPlanDetailsLoaded(false);
    try {
      const res = await apiRequest("get", "/clients/:client_id/plan", {
        params: { client_id: data._id },
      });
      if (res?.data?.status === 200) {
        setPlanDetails(res?.data?.data);
      } else {
        setPlanDetails(null);
      }
    } catch (err) {
      setPlanDetails(null);
      // console.error("Error fetching plan details:", err);
    } finally {
      setPlanDetailsLoaded(true);
    }
  }, [data?._id]);

  useEffect(() => {
    getPlanDetails();
  }, [getPlanDetails]);

  useEffect(() => {
    const newTabs = [];

    if (data?.status !== "active" && data?.status !== "inactive") {
      newTabs.push({
        name: "Setup Steps",
        icon: Cog8ToothIcon,
        component: (
          <ClientSetup
            details={data}
            statuses={statuses}
            setLists={setLists}
          />
        ),
      });
    }

    newTabs.push({
      name: "Overview",
      icon: Squares2X2Icon,
      component: (
        <ClientOverview
          data={data}
          defaultUser={defaultUser}
          domain={domain}
          isEditing={isEditing}
          clientTypes={clientTypes}
          handleTypeChange={handleClientTypeChange}
          setShowPlanSelection={setShowPlanSelection}
        />
      ),
    });

    if (data?.status === "active" || data?.status === "inactive") {
      newTabs.push({
        name: "Environment Setup",
        icon: GlobeAltIcon,
        component: <EnvironmentSetup client={data} />,
      });

      newTabs.push({
        name: "Admins",
        icon: UsersIcon,
        component: (
          <ClientUserList
            key={data?._id}
            client={data}
          />
        ),
      });

      if (data?.client_type === "subscription") {
        newTabs.push({
          name: "Plan Details",
          icon: Square3Stack3DIcon,
          component: (
            <>
              {planDetailsLoaded ? (
                <ClientPlanDetails
                  client={data}
                  planDetails={planDetails?.active_plan}
                  showPlanSelection={showPlanSelection}
                  setShowPlanSelection={setShowPlanSelection}
                />
              ) : (
                <Preloader />
              )}
            </>
          ),
        });
      }
    }

    setTabs(newTabs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, domain, planDetailsLoaded, planDetails, defaultUser]);

  useEffect(() => {
    if (tabs.length > 0) {
      setCurrentTab(tabs[0]);
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("tab");
      window.history.replaceState({}, "", currentUrl.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?._id, tabs.length]);

  const handleClientTypeChange = async (updatedData) => {
    if (updatedData.triggerPlanAssign) {
      setShowPlanSelection(true);
      return;
    }

    const result = handleTypeChange(updatedData);
    if (result === "success") {
      await getPlanDetails();
    }
  };

  return (
    <div>
      <div className="main_item-background relative px-8 pb-6 pt-8">
        <div className="mb-5 flex items-center space-x-4">
          <h1 className="text-4xl font-bold text-gray-900">{data?.company_name || data?.name}</h1>
          <div className="flex items-center">
            <div className={classNames("flex h-8 items-center justify-center gap-2 rounded border px-2", statuses[data?.status]?.class)}>{statuses[data?.status]?.text}</div>
          </div>
        </div>
      </div>
      <div className="px-6 pb-4">
        <Tabs
          currentTab={currentTab}
          tabs={tabs}
        />
      </div>
      <AssignPlanSlider
        restrictPlanChange
        isOpen={showPlanSelection}
        plans={plans}
        client={data}
        planDetails={planDetails}
        onClose={() => setShowPlanSelection(false)}
        onPlanChange={async (plan_id) => {
          try {
            const result = handleTypeChange({ client_type: "subscription", plan_id });
            if (result === "success") {
              await getPlanDetails();
            }
          } catch (err) {
            // console.error("Error updating client type:", err);
          }
        }}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  site: state.site,
});

export default connect(mapStateToProps)(ClientBodyContent);
